import React from 'react';
import {
  Table, Button, Card, Row, Col, Tag, Modal, Spin, Alert,
} from 'antd';
import { connect } from 'react-redux';
import * as userActions from '../../actions/user-action-types';
import DashBoard from '../dashboard/Dashboard';

// Keys are camelCase: parkao-api's sendRes camel-cases every response key
// (utilities/common.js, utilities/snakeCase.js keysToCamel).

// parkao-api groups draft spots by location.state (and postal code) and
// labels a null state 'Unknown'; an empty state ('' is the model default)
// arrives as ''. Activation and launch emails take the state the panel sends,
// trimmed by Joi, and match it exactly. So none of these can be launched from
// here: '' fails Joi (400), 'Unknown' matches no spot, and a padded state is
// trimmed into a different state (or none) before the query.
const UNKNOWN_CITY = 'Unknown';
const NO_STATE_NOTE = 'No state on these spots: they cannot be launched by city.';
const PADDED_STATE_NOTE = 'The state has stray spaces, which the API trims: these spots cannot be launched by city.';
const isLaunchable = (city) => typeof city === 'string' && city !== '' && city !== UNKNOWN_CITY
  && city === city.trim();
const isPadded = (city) => typeof city === 'string' && city.trim() !== '' && city !== UNKNOWN_CITY
  && city !== city.trim();

// The API answers 200 even when it skipped spots: they stay drafts.
const activationOutcome = (result) => {
  if (!result || !result.totalFound) {
    return 'none';
  }
  if (result.skippedCount > 0 || result.activatedCount < result.totalFound) {
    return 'partial';
  }
  return 'complete';
};

const isAnswer = (status) => status === 'success' || status === 'failed';

const plural = (count, word) => `${count} ${count === 1 ? word : `${word}s`}`;

const STOPPED = 'Full Launch stopped before the launch emails';
const EMAILS_CONTENT = 'This will send a launch notification email to all verified landing users who have spots in this city. This action cannot be undone.';

// Each activation and send carries an id that comes back with its outcome.
// Unique across reloads (the store is persisted), increasing within a page.
let lastRequestId = 0;
const nextRequestId = () => {
  lastRequestId = Math.max(lastRequestId + 1, Date.now());
  return lastRequestId;
};

class LaunchManagement extends React.PureComponent {
  state = {
    // The activation started from this page:
    // { requestId, city, fullLaunch, settled, outcome, result, stopNote }.
    activation: null,
    // Launch emails sent from this page, by city:
    // { requestId, outcome: 'pending' | 'success' | 'failed' }.
    sends: {},
    // The latest of those sends: { requestId, city }.
    lastSend: null,
  };

  componentDidMount() {
    const { fetchDraftSpotsByCity } = this.props;
    fetchDraftSpotsByCity();
  }

  componentDidUpdate() {
    this.settleActivation();
    this.settleSend();
  }

  settleActivation() {
    const {
      activateCitySpotsStatus: status,
      activateCitySpotsResult: result,
      activateCitySpotsRequestId: answeredId,
    } = this.props;
    const { activation, sends } = this.state;
    // Only this activation's own answer counts, never a result kept (and
    // persisted) from an earlier one.
    if (!activation || activation.settled || answeredId !== activation.requestId || !isAnswer(status)) {
      return;
    }

    const outcome = status === 'failed' ? 'failed' : activationOutcome(result);
    let stopNote = null;
    if (activation.fullLaunch) {
      if (outcome === 'partial') {
        stopNote = `${STOPPED}: the owners of the skipped spots would get them too.`;
      } else if (outcome !== 'complete') {
        stopNote = `${STOPPED}.`;
      } else if (sends[activation.city]) {
        stopNote = `${STOPPED}: they were already sent for ${activation.city} from this page.`;
      }
    }
    this.setState({
      activation: {
        ...activation, settled: true, outcome, result: status === 'success' ? result : null, stopNote,
      },
    });
    // Launch emails go to the owners of every landing spot in the city that is
    // a draft or live, so Full Launch offers them only when every draft spot
    // found was activated, and only if none were sent for the city already.
    if (activation.fullLaunch && !stopNote) {
      this.confirmLaunchEmails(activation.city, 0);
    }
  }

  settleSend() {
    const { sendLaunchEmailsStatus: status, sendLaunchEmailsRequestId: answeredId } = this.props;
    const { sends } = this.state;
    const city = Object.keys(sends).find((c) => sends[c].requestId === answeredId);
    if (city === undefined || sends[city].outcome !== 'pending' || !isAnswer(status)) {
      return;
    }
    this.setState({ sends: { ...sends, [city]: { ...sends[city], outcome: status } } });
  }

  draftsIn = (city) => {
    const { draftSpotsByCity } = this.props;
    return (draftSpotsByCity || [])
      .filter((group) => group.city === city)
      .reduce((sum, group) => sum + group.count, 0);
  }

  startActivation = (city, fullLaunch) => {
    const { activateCitySpots } = this.props;
    const requestId = nextRequestId();
    this.setState({
      activation: {
        requestId, city, fullLaunch, settled: false,
      },
    });
    activateCitySpots({ city, requestId });
  }

  startSend = (city) => {
    const { sendLaunchEmails } = this.props;
    const requestId = nextRequestId();
    this.setState(({ sends }) => ({
      sends: { ...sends, [city]: { requestId, outcome: 'pending' } },
      lastSend: { requestId, city },
    }));
    sendLaunchEmails({ city, requestId });
  }

  handleActivateCity = (city) => {
    Modal.confirm({
      title: `Activate all spots in "${city}"?`,
      content: 'This will publish all draft landing spots that have the minimum required data (address, coordinates, levels). Spots missing data will remain as drafts.',
      okText: 'Yes, Activate',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: () => {
        this.startActivation(city, false);
      },
    });
  }

  // `drafts`: spots of this city still in draft. The API emails their owners
  // too (sendLaunchEmails picks spots with status 1 or 2).
  confirmLaunchEmails = (city, drafts) => {
    const { sends } = this.state;
    const previous = sends[city];
    const notes = [];
    if (drafts === 1) {
      notes.push(`1 spot in ${city} is still a draft; its owner will get this email too.`);
    } else if (drafts > 1) {
      notes.push(`${drafts} spots in ${city} are still drafts; their owners will get this email too.`);
    }
    if (previous && previous.outcome === 'failed') {
      notes.push(`A previous send for ${city} from this page failed; some owners may already have this email.`);
    } else if (previous) {
      notes.push(`Launch emails for ${city} were already sent from this page.`);
    }
    Modal.confirm({
      title: `Send launch emails to all owners in "${city}"?`,
      content: [...notes, EMAILS_CONTENT].join(' '),
      okText: 'Yes, Send Emails',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        this.startSend(city);
      },
    });
  }

  handleLaunchCity = (city) => {
    Modal.confirm({
      title: `Full Launch: "${city}"`,
      content: 'This will: 1) Activate all eligible draft spots, then 2) if every draft spot was activated, ask you to confirm the launch emails to all verified owners. Proceed?',
      okText: 'Launch City',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        this.startActivation(city, true);
      },
    });
  }

  renderActivationResult(busy) {
    const { activation, sends } = this.state;
    if (!activation || !activation.settled) {
      return null;
    }
    const {
      city, outcome, result, stopNote,
    } = activation;
    const alertProps = { style: { marginBottom: 16 }, showIcon: true, closable: true };
    const withNote = (text) => (stopNote ? `${text} ${stopNote}` : text);

    if (outcome === 'failed') {
      return (
        <Alert
          {...alertProps}
          type="error"
          message={`Activation failed for ${city}.`}
          description={withNote('Refresh the list to see which spots are still drafts.')}
        />
      );
    }
    if (outcome === 'none') {
      return (
        <Alert
          {...alertProps}
          type="warning"
          message={`No draft spots found in ${city}. Nothing was activated.`}
          description={stopNote || undefined}
        />
      );
    }

    const message = `Activated ${result.activatedCount} of ${result.totalFound} spots in ${city}`;
    if (outcome === 'partial') {
      const skipped = (result.skipped || [])
        .map((spot) => `${spot.title || 'Untitled spot'}: ${(spot.missing || []).join(', ')}`)
        .join(' | ');
      return (
        <Alert
          {...alertProps}
          type="warning"
          message={message}
          description={withNote(`${plural(result.skippedCount, 'spot')} skipped (missing data): ${skipped}`)}
        />
      );
    }

    // Complete: the city has no drafts left, so it drops off the table; the
    // launch emails are offered here unless they were sent from this page.
    const previous = sends[city];
    const offerSend = !previous || previous.outcome === 'failed';
    return (
      <Alert
        {...alertProps}
        type="success"
        message={message}
        description={(
          <span>
            {withNote('All spots activated successfully.')}
            {offerSend && (
              <Button
                size="small"
                style={{ marginLeft: 8 }}
                disabled={busy}
                onClick={() => this.confirmLaunchEmails(city, this.draftsIn(city))}
              >
                Send launch emails
              </Button>
            )}
          </span>
        )}
      />
    );
  }

  renderSendResult() {
    const {
      sendLaunchEmailsStatus: status,
      sendLaunchEmailsResult: result,
      sendLaunchEmailsRequestId: answeredId,
    } = this.props;
    const { lastSend } = this.state;
    // Only a send made from this page, not one kept from an earlier visit.
    if (!lastSend || answeredId !== lastSend.requestId || status !== 'success' || !result) {
      return null;
    }
    return (
      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        closable
        message={`Sent ${result.emailsSent} launch emails for ${lastSend.city}`}
      />
    );
  }

  render() {
    const {
      draftSpotsByCity,
      draftSpotsByCityStatus,
      activateCitySpotsStatus,
      sendLaunchEmailsStatus,
      fetchDraftSpotsByCity,
    } = this.props;

    const cities = draftSpotsByCity || [];
    const isLoading = draftSpotsByCityStatus === 'requested';
    const isActivating = activateCitySpotsStatus === 'requested';
    const isSending = sendLaunchEmailsStatus === 'requested';
    // One activation or send at a time: the sagas keep only the latest of
    // each, and the API does not deduplicate launch emails.
    const busy = isActivating || isSending;

    const columns = [
      {
        title: 'City / State',
        dataIndex: 'city',
        key: 'city',
        render: (city) => (isLaunchable(city) ? <strong>{city}</strong> : (
          <span>
            <strong>{isPadded(city) ? city : UNKNOWN_CITY}</strong>
            <div style={{ fontSize: 12, color: '#888' }}>
              {isPadded(city) ? PADDED_STATE_NOTE : NO_STATE_NOTE}
            </div>
          </span>
        )),
      },
      {
        title: 'Draft Spots',
        dataIndex: 'count',
        key: 'count',
        sorter: (a, b) => a.count - b.count,
        render: (count) => <Tag color="orange">{count}</Tag>,
      },
      {
        title: 'Owners',
        dataIndex: 'ownersCount',
        key: 'ownersCount',
      },
      {
        title: 'With Photos',
        dataIndex: 'spotsWithPhotos',
        key: 'spotsWithPhotos',
        render: (val, record) => (
          <span>
            {val} <Tag color={val === record.count ? 'green' : 'blue'}>
              {record.count ? Math.round((val / record.count) * 100) : 0}%
            </Tag>
          </span>
        ),
      },
      {
        title: 'With Price',
        dataIndex: 'spotsWithPrice',
        key: 'spotsWithPrice',
        render: (val, record) => (
          <span>
            {val} <Tag color={val === record.count ? 'green' : 'blue'}>
              {record.count ? Math.round((val / record.count) * 100) : 0}%
            </Tag>
          </span>
        ),
      },
      {
        title: 'Sample Addresses',
        dataIndex: 'sampleAddresses',
        key: 'sampleAddresses',
        render: (addrs) => (
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
            {(addrs || []).map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => {
          const disabled = !isLaunchable(record.city) || busy;
          return (
            <span>
              <Button
                type="primary"
                size="small"
                style={{ marginRight: 8, marginBottom: 4 }}
                onClick={() => this.handleActivateCity(record.city)}
                loading={isActivating}
                disabled={disabled}
              >
                Activate Spots
              </Button>
              <Button
                size="small"
                style={{ marginRight: 8, marginBottom: 4 }}
                onClick={() => this.confirmLaunchEmails(record.city, this.draftsIn(record.city))}
                loading={isSending}
                disabled={disabled}
              >
                Send Emails
              </Button>
              <Button
                type="danger"
                size="small"
                onClick={() => this.handleLaunchCity(record.city)}
                loading={busy}
                disabled={disabled}
              >
                Full Launch
              </Button>
            </span>
          );
        },
      },
    ];

    const totalDraftSpots = cities.reduce((sum, c) => sum + c.count, 0);
    const totalOwners = cities.reduce((sum, c) => sum + c.ownersCount, 0);
    // Cities, not city + postal-code rows, and only ones this page can launch.
    const readyCities = new Set(cities
      .filter((c) => isLaunchable(c.city) && c.spotsWithPrice > 0)
      .map((c) => c.city)).size;

    return (
      <DashBoard selectKey="launch_management" {...this.props}>
        <div className="tableHeaderText">
          <div className="tableMainHead">
            <p className="titleTextStyle">Launch Management</p>
          </div>
          <div>
            <Button
              onClick={() => fetchDraftSpotsByCity()}
              loading={isLoading}
            >
              Refresh
            </Button>
          </div>
        </div>
        <br />

        {/* Summary */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <h4>Cities with Draft Spots</h4>
              <p style={{ fontSize: 28, fontWeight: 'bold', margin: 0 }}>
                {cities.length}
              </p>
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <h4>Total Draft Spots</h4>
              <p style={{ fontSize: 28, fontWeight: 'bold', margin: 0 }}>
                {totalDraftSpots}
              </p>
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <h4>Total Owners</h4>
              <p style={{ fontSize: 28, fontWeight: 'bold', margin: 0 }}>
                {totalOwners}
              </p>
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <h4>Ready to Launch</h4>
              <p style={{ fontSize: 28, fontWeight: 'bold', margin: 0 }}>
                {`${readyCities} ${readyCities === 1 ? 'city' : 'cities'}`}
              </p>
            </Card>
          </Col>
        </Row>

        {/* Activation result */}
        {this.renderActivationResult(busy)}

        {/* Email result */}
        {this.renderSendResult()}

        {/* Cities Table */}
        <Spin spinning={isLoading}>
          <Card>
            <h3 style={{ marginBottom: 16 }}>Draft Spots by City</h3>
            <Table
              columns={columns}
              dataSource={cities.map((c, i) => ({ ...c, key: i }))}
              pagination={false}
              size="middle"
            />
          </Card>
        </Spin>
      </DashBoard>
    );
  }
}

const mapStateToProps = ({
  user: {
    draftSpotsByCityStatus,
    draftSpotsByCity,
    activateCitySpotsStatus,
    activateCitySpotsResult,
    activateCitySpotsRequestId,
    sendLaunchEmailsStatus,
    sendLaunchEmailsResult,
    sendLaunchEmailsRequestId,
  },
}) => ({
  draftSpotsByCityStatus,
  draftSpotsByCity,
  activateCitySpotsStatus,
  activateCitySpotsResult,
  activateCitySpotsRequestId,
  sendLaunchEmailsStatus,
  sendLaunchEmailsResult,
  sendLaunchEmailsRequestId,
});

const mapDispatchToProps = {
  fetchDraftSpotsByCity: userActions.fetchDraftSpotsByCity,
  activateCitySpots: userActions.activateCitySpots,
  sendLaunchEmails: userActions.sendLaunchEmails,
};

export default connect(mapStateToProps, mapDispatchToProps)(LaunchManagement);
